import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { EVIDENCE_DETECTIVE_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * evidence_detective — reveal clues, narrow suspects, pick the best answer.
 *
 * Reuse: planet from clues; animal from tracks; sensor from symptoms; pattern rule from examples (all JSON).
 *
 * activityData.content schema:
 * {
 *   instructions?: string,
 *   clues: [{ id: string, text: string }],
 *   choices: [
 *     { id: string, text: string, correct?: boolean, eliminatedAfterClueCount?: number }
 *   ],
 *   minCluesBeforeSubmit?: number,   // default 1
 *   solutionExplanation?: string,
 *   victory?: { message?: string }
 * }
 *
 * Tapping a clue marks it revealed. Wrong choices with eliminatedAfterClueCount become disabled
 * after that many clues have been revealed. Submit checks the selected radio against correct: true.
 */

function pickEvidenceTitle(titleProp, guidedContext) {
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const activityTitle = typeof titleProp === 'string' ? titleProp.trim() : '';
  if (guidedContext?.singleSurface === true && stepTitle.trim()) {
    if (!activityTitle) return null;
    if (shouldSuppressActivityTitle({ stepTitle, activityTitle: titleProp })) return null;
    return activityTitle;
  }
  if (activityTitle) return activityTitle;
  const fallback = typeof titleProp === 'string' && titleProp.trim() ? titleProp.trim() : 'Evidence detective';
  return fallback;
}

function pickInstructionLine(guidedContext, rawInstructions, compactLesson) {
  const readable =
    typeof guidedContext?.readableInstruction === 'string' ? guidedContext.readableInstruction.trim() : '';
  const lead = typeof guidedContext?.stepInstructionLead === 'string' ? guidedContext.stepInstructionLead.trim() : '';
  const shellOneShot = compactLesson && guidedContext?.singleSurface === true;

  if (shellOneShot) {
    if (readable) return readable;
    if (lead) return lead;
    if (rawInstructions) return rawInstructions;
    return '';
  }
  if (lead) return lead;
  if (rawInstructions) return rawInstructions;
  return '';
}

export default function EvidenceDetectiveEngine({
  title = 'Evidence detective',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_evidence_detective',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const clues = Array.isArray(content.clues) ? content.clues : [];
  const choices = Array.isArray(content.choices) ? content.choices : [];
  const minClues = Math.max(1, Number(content.minCluesBeforeSubmit) || 1);

  const [revealed, setRevealed] = useState(() => new Set());
  const [selected, setSelected] = useState('');
  const [completed, setCompleted] = useState(false);
  const [wrong, setWrong] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');

  const singleSurface = guidedContext?.singleSurface === true;
  const tapCompact = singleSurface && isTapFirstLessonUi(lessonUiMode);
  const shellDeferSubmit = typeof onRegisterLessonFooterActions === 'function' && singleSurface;

  const compactLesson = isTapFirstLessonUi(lessonUiMode);
  const rawInstructions = typeof content.instructions === 'string' ? content.instructions.trim() : '';

  const headline = useMemo(() => pickEvidenceTitle(title, guidedContext), [title, guidedContext]);
  const instructionBody = useMemo(
    () => pickInstructionLine(guidedContext, rawInstructions, compactLesson),
    [guidedContext, rawInstructions, compactLesson],
  );

  const revealedCount = revealed.size;

  const correctId = useMemo(() => {
    const c = choices.find((ch) => ch.correct);
    return c?.id || '';
  }, [choices]);

  const choiceDisabled = useCallback(
    (ch) => {
      if (ch.correct) return false;
      const n = Number(ch.eliminatedAfterClueCount);
      if (!Number.isFinite(n)) return false;
      return revealedCount >= n;
    },
    [revealedCount],
  );

  const getExplanation = useCallback(() => content.solutionExplanation || content.explanation, [content]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: EVIDENCE_DETECTIVE_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer } = guided;

  const reveal = (id) => {
    if (completed) return;
    noteUserAction();
    setWrong(false);
    setRevealed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    onProgress?.({ activityId, engine: 'evidence_detective', phase: 'clue', id });
  };

  const submit = useCallback(() => {
    if (completed) return;
    noteUserAction();
    if (revealedCount < minClues) {
      setA11y(`Reveal at least ${minClues} clue${minClues > 1 ? 's' : ''} first.`);
      return;
    }
    if (!selected) {
      setWrong(true);
      setA11y('Pick an answer.');
      return;
    }
    if (selected === correctId) {
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('Case closed!');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'evidence_detective', perfect: true });
    } else {
      setWrong(true);
      noteIncorrectAnswer();
      setA11y('Try another answer.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    activityId,
    celebrate,
    completed,
    correctId,
    minClues,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    noteUserAction,
    onComplete,
    revealedCount,
    selected,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    setRevealed(new Set(clues.map((c) => c.id)));
    setSelected(correctId);
    setWrong(false);
  }, [clues, completed, correctId, noteUserAction]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions || !shellDeferSubmit) return undefined;
    if (completed) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer = Array.isArray(hints) && hints.length > 0 ? revealAnswer : undefined;
    onRegisterLessonFooterActions({
      checkAnswer: submit,
      checkAnswerDisabled: completed || revealedCount < minClues,
      checkAnswerLabel: 'Submit answer',
      ...(showAnswer ? { showAnswer } : {}),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    completed,
    hints.length,
    minClues,
    onRegisterLessonFooterActions,
    revealAnswer,
    revealedCount,
    shellDeferSubmit,
    submit,
  ]);

  const titleStyleUse =
    tapCompact && headline
      ? { ...titleStyle, margin: '0 0 6px', fontSize: 'clamp(1rem, 3vw, 1.18rem)' }
      : titleStyle;
  const instructStyleUse = tapCompact
    ? { ...instructionsStyle, fontSize: 14, lineHeight: 1.35, marginBottom: 8 }
    : instructionsStyle;

  const rootStyle = {
    ...getEngineRootStyle({ singleSurface }),
    position: 'relative',
    ...(guided.contentReserveStyle || {}),
  };

  const sectionGap = tapCompact ? 8 : 14;
  const sectionLabel = { fontWeight: 800, marginBottom: tapCompact ? 4 : 8, fontSize: tapCompact ? 12 : 14 };

  return (
    <div style={rootStyle}>
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Mystery solved!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {headline ? <h2 style={titleStyleUse}>{headline}</h2> : null}
      {instructionBody ? <p style={instructStyleUse}>{instructionBody}</p> : null}

      <div style={{ marginBottom: sectionGap }}>
        <div style={sectionLabel}>Clues</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: tapCompact ? 6 : 8 }}>
          {clues.map((cl, clueIdx) => {
            const isRev = revealed.has(cl.id);
            return (
              <button
                key={cl.id}
                type="button"
                disabled={completed || isRev}
                onClick={() => reveal(cl.id)}
                aria-label={isRev ? cl.text : `Reveal clue ${clueIdx + 1}`}
                style={{
                  ...secondaryBtn,
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: tapCompact ? 14 : undefined,
                  minHeight: tapCompact ? 40 : 44,
                  padding: tapCompact ? '8px 10px' : undefined,
                  opacity: isRev ? 1 : 0.95,
                  borderStyle: isRev ? 'solid' : 'dashed',
                  background: isRev ? 'rgba(99,102,241,0.1)' : 'var(--color-bg)',
                }}
              >
                {isRev ? `🔎 ${cl.text}` : `Clue ${clueIdx + 1}`}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: sectionGap }}>
        <div style={sectionLabel}>Best answer</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: tapCompact ? 6 : 8 }}>
          {choices.map((ch) => {
            const grey = choiceDisabled(ch);
            return (
              <label
                key={ch.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: tapCompact ? 8 : 10,
                  padding: tapCompact ? '8px 10px' : '10px 12px',
                  borderRadius: 12,
                  border: `2px solid ${selected === ch.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: grey ? 'rgba(148,163,184,0.2)' : 'var(--color-bg)',
                  cursor: grey || completed ? 'default' : 'pointer',
                  opacity: grey ? 0.45 : 1,
                }}
              >
                <input
                  type="radio"
                  name="evidence-choice"
                  value={ch.id}
                  checked={selected === ch.id}
                  disabled={completed || grey}
                  onChange={() => {
                    noteUserAction();
                    setSelected(ch.id);
                  }}
                  style={{ marginTop: tapCompact ? 2 : 3 }}
                />
                <span style={{ fontWeight: 600, fontSize: tapCompact ? 14 : 15 }}>{ch.text}</span>
              </label>
            );
          })}
        </div>
      </div>

      {wrong && (
        <p style={{ ...feedbackIncorrect, fontSize: tapCompact ? 13 : undefined, marginBottom: tapCompact ? 6 : undefined }}>
          Not quite — use the clues and try again.
        </p>
      )}
      {completed && (
        <p style={{ ...feedbackCorrect, marginBottom: tapCompact ? 8 : 10, fontSize: tapCompact ? 13 : undefined }}>
          {content.solutionExplanation || 'Great reasoning!'}
        </p>
      )}

      {!shellDeferSubmit ? (
        <button type="button" style={primaryBtn} onClick={submit} disabled={completed}>
          Submit answer
        </button>
      ) : null}
    </div>
  );
}
